import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import type { ExportFormat } from "../../lib/export";
import { SIDEBAR_VIEWS, type SidebarViewId } from "../../lib/sidebarViews";
import { useIsPro } from "../../store/subscription";
import {
  useUIStore,
  type NotePreviewStyle,
  type NoteSortField,
} from "../../store/ui";
import { ProTag } from "../ui/ProTag";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { VIEW_LABEL_KEYS } from "./SidebarViewsSection";
import { Icon } from "@/components/ui/Icon";

interface NoteListMenuProps {
  title: string;
  noteCount: number;
  onSelectNotes?: () => void;
  /** Export every note in view in the chosen format (parent owns the wiring). */
  onExportAll?: (format: ExportFormat) => void;
  /** Active tag id, when the list is showing a tag view. */
  tagId?: string;
  /** Open the tag rename dialog (owned by the parent). */
  onRenameTag?: () => void;
  /** Open the tag appearance editor (owned by the parent). */
  onEditTagAppearance?: () => void;
  /** Which edge of the trigger the menu lines up with. */
  align?: "start" | "end";
}

export function NoteListMenu({
  title,
  noteCount,
  onSelectNotes,
  onExportAll,
  tagId,
  onRenameTag,
  onEditTagAppearance,
  align = "start",
}: NoteListMenuProps) {
  const { t } = useTranslation();
  const isPro = useIsPro();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { id: noteId } = useParams<{ id?: string }>();

  const noteSortField = useUIStore((s) => s.noteSortField);
  const notePreviewStyle = useUIStore((s) => s.notePreviewStyle);
  const setNoteSortField = useUIStore((s) => s.setNoteSortField);
  const setNotePreviewStyle = useUIStore((s) => s.setNotePreviewStyle);
  const sidebarViewOrder = useUIStore((s) => s.sidebarViewOrder);
  const sidebarViewHidden = useUIStore((s) => s.sidebarViewHidden);

  const hiddenSet = new Set(sidebarViewHidden);

  // Mirror the sidebar's link building so switching a view preserves the open note.
  const basePath = noteId ? `/app/note/${noteId}` : "/app";
  const buildHref = (params: string | null) =>
    params ? `${basePath}?${params}` : basePath;

  const currentView = searchParams.get("view");
  const currentTag = searchParams.get("tag");
  const isAppPath =
    location.pathname === "/app" || location.pathname.startsWith("/app/note/");

  const isViewActive = (id: SidebarViewId): boolean => {
    if (!isAppPath) return false;
    if (id === "all") return !currentView && !currentTag;
    return currentView === id && !currentTag;
  };

  const showTagSection = Boolean(tagId && (onRenameTag || onEditTagAppearance));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="header"
          title={t("toolbar.sortBy")}
          aria-label={t("toolbar.sortBy")}
        >
          <Icon name="sort" size={18} className="text-ink" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side="bottom" className="min-w-[200px]">
        <DropdownMenuLabel className="text-xs text-muted font-normal">
          {t("noteList.noteCount", { count: noteCount })}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Views */}
        <DropdownMenuLabel className="text-[11px] text-muted font-medium uppercase tracking-wider">
          {t("sidebar.views")}
        </DropdownMenuLabel>
        {sidebarViewOrder
          .filter((id) => !hiddenSet.has(id))
          .map((id) => {
            const def = SIDEBAR_VIEWS[id];
            if (!def) return null;
            return (
              <DropdownMenuItem
                key={id}
                onSelect={() => navigate(buildHref(def.queryString))}
                className={cn(isViewActive(id) && "text-accent-text bg-active")}
              >
                <Icon name={def.icon} size={15} />
                {t(VIEW_LABEL_KEYS[id])}
              </DropdownMenuItem>
            );
          })}

        {/* Tag actions (only on a tag view) */}
        {showTagSection && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] text-muted font-medium tracking-wider truncate">
              {title}
            </DropdownMenuLabel>
            {onRenameTag && (
              <DropdownMenuItem onSelect={onRenameTag}>
                <Icon name="edit" size={15} />
                {t("tagActions.rename")}
              </DropdownMenuItem>
            )}
            {onEditTagAppearance && (
              <DropdownMenuItem onSelect={onEditTagAppearance}>
                <Icon name="palette" size={15} />
                {t("tagActions.editAppearance")}
              </DropdownMenuItem>
            )}
          </>
        )}

        {(onSelectNotes || onExportAll) && <DropdownMenuSeparator />}
        {onSelectNotes && (
          <DropdownMenuItem onSelect={onSelectNotes}>
            <Icon name="checkbox" size={15} />
            {t("noteList.selectNotes")}
          </DropdownMenuItem>
        )}
        {onExportAll && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Icon name="export" size={15} />
              {t("noteList.exportAll")}
              <span className="ml-auto flex items-center gap-1">
                {!isPro && <ProTag />}
                <Icon name="chevron-right" size={14} className="text-muted" />
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onSelect={() => onExportAll("markdown")}>
                {t("noteActions.exportMarkdown")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExportAll("txt")}>
                {t("noteActions.exportPlainText")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExportAll("textpack")}>
                {t("noteActions.exportTextpack")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onExportAll("html")}>
                {t("noteActions.exportHtml")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExportAll("rtf")}>
                {t("noteActions.exportRtf")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExportAll("epub")}>
                {t("noteActions.exportEpub")}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <DropdownMenuSeparator />

        {/* Sort By */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Icon name="sort" size={15} />
            {t("noteList.sortBy")}
            <Icon name="chevron-right" size={14} className="ml-auto text-muted" />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={noteSortField}
              onValueChange={(v) => setNoteSortField(v as NoteSortField)}
            >
              <DropdownMenuRadioItem value="updatedAt">
                {t("noteList.modificationDate")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="createdAt">
                {t("noteList.creationDate")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="title">
                {t("noteList.title")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="manual">
                {t("noteList.manual")}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* View → Density */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Icon name="layout-list" size={15} />
            {t("noteList.view")}
            <Icon name="chevron-right" size={14} className="ml-auto text-muted" />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuLabel className="text-[11px] text-muted font-medium uppercase tracking-wider">
              {t("noteList.density")}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={notePreviewStyle}
              onValueChange={(v) => setNotePreviewStyle(v as NotePreviewStyle)}
            >
              <DropdownMenuRadioItem value="small">
                {t("noteList.small")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="medium">
                {t("noteList.medium")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="large">
                {t("noteList.large")}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
